import type { CSSProperties, FC, ReactNode } from 'react';
import { useCallback, useEffect, useRef } from 'react';
import Popup from 'reactjs-popup';
import { Picture } from '@ui';
import { arrowLeftLong } from '@images';
import icons from '@icons';
import { twcls } from '@utils/twcls';
import { lockBodyScroll, unlockBodyScroll } from '@utils/scrollLock';
import useWindowSize from '@customHooks/useWindowSize';
import useFocusTrap from '@customHooks/useFocusTrap';
import type { PopupProps } from 'reactjs-popup/dist/types';
import type { ClassValue } from 'clsx';
import './Modal.scss';

const {
  close: { midGrey: closeOutlineMidGrey, darkGrey: closeOutlineDG }
} = icons.Outline;

export const MODAL_CROSS_CLASSES =
  'cursor-pointer absolute top-6 right-6 z-[10000] sm:top-4 sm:right-4';

const MODAL_BACK_CLASSES = 'absolute top-6 left-6 z-10 sm:top-4 sm:left-4';

export type ModalPlacement = 'center' | 'bottom-sheet' | 'fullscreen' | 'top';

const TOP_PLACEMENT_OFFSET = '70px';

const SHEET_TOP_OFFSET = '40px';

const SHEET_POPUP_CLASS = 'Modal-sheet';

const PLACEMENT_CONTENT_STYLE: Record<ModalPlacement, CSSProperties> = {
  center: {
    borderRadius: '32px'
  },
  'bottom-sheet': {
    position: 'fixed',
    WebkitBackfaceVisibility: 'hidden',
    bottom: '0',
    left: '0',
    width: '100%',
    maxHeight: `calc(100% - ${SHEET_TOP_OFFSET})`,
    overscrollBehavior: 'none',
    overflowY: 'scroll',
    overflowX: 'hidden',
    overflow: 'auto',
    borderRadius: '8px 8px 0 0'
  },
  fullscreen: {
    width: '100%',
    height: '100%',
    borderRadius: 0
  },
  top: {
    position: 'fixed',
    top: TOP_PLACEMENT_OFFSET,
    left: '50%',
    transform: 'translateX(-50%)',
    width: 'auto',
    height: 'auto',
    maxHeight: `calc(100vh - ${TOP_PLACEMENT_OFFSET})`,
    overflow: 'auto',
    borderRadius: '32px'
  }
};

export interface ModalProps {
  /** Whether the modal is visible. Omit when using a `trigger` instead. */
  open?: boolean;
  /** Modal body, or a render function receiving the imperative `close`. */
  children: ReactNode | ((close: () => void) => ReactNode);
  /** Called whenever the modal requests to close (cross, ESC, outside click). */
  onClose?: () => void;
  /** Optional back button handler (shows the back arrow when provided). */
  onBack?: () => void;
  /** Accessible name for the dialog (read by screen readers). Prefer
   *  `ariaLabelledby` when a visible heading exists. */
  ariaLabel?: string;
  /** id of the visible element that titles the dialog. */
  ariaLabelledby?: string;
  /** Hide the close cross. */
  withoutCross?: boolean;
  /** Render the standard white framed box (bg/padding/radius). Off by default
   *  so transparent modals (video, iframe) can supply their own layout. */
  framed?: boolean;
  /** Tailwind width/height utilities for the modal box (e.g.
   *  `w-[332px] sm:w-[244px]`). This is the declarative replacement for the
   *  per-modal SCSS `width`/`min-width`/`max-width`/`height` rules. Placement is
   *  controlled by `placement*`, chrome by `framed`. When omitted, `framed`
   *  falls back to its legacy `max-w-[480px]` default. */
  size?: ClassValue;
  /** Class on the modal content box. */
  className?: ClassValue;
  /** Class on the close cross. */
  closeClassName?: ClassValue;
  crossOutside?: boolean;
  contentStyle?: CSSProperties;
  overlayStyle?: CSSProperties;
  /** Modal docking per breakpoint. Each defaults to 'center'.
   *  mobile <768 · tablet 768–1279 · desktop ≥1280. */
  placementMobile?: ModalPlacement;
  placementTablet?: ModalPlacement;
  placementDesktop?: ModalPlacement;
  /** Raw `reactjs-popup` props forwarded to the underlying `<Popup>` — e.g.
   *  `trigger`, `modal` (default true), `lockScroll` (default true),
   *  `closeOnDocumentClick` (default true), `arrow`, `repositionOnResize`.
   *  Modal's own props (open, onClose, content/overlayStyle) take precedence. */
  popupProps?: Omit<
    PopupProps,
    | 'children'
    | 'className'
    | 'open'
    | 'onClose'
    | 'contentStyle'
    | 'overlayStyle'
  >;
}

/** Reset for the icon-only `<button>`s wrapping the close/back pictures. */
const ICON_BUTTON_RESET =
  'border-0 bg-transparent p-0 cursor-pointer leading-none';

interface ModalContentProps {
  activePlacement: ModalPlacement;
  framed: boolean;
  size?: ClassValue;
  className?: ClassValue;
  onBack?: () => void;
  withoutCross: boolean;
  crossOutside?: boolean;
  closeClassName?: ClassValue;
  ariaLabel?: string;
  ariaLabelledby?: string;
  children: ReactNode | ((close: () => void) => ReactNode);
  /** reactjs-popup's imperative close, available only while the popup is open. */
  close: () => void;
  /** Runs the consumer `onClose` and then the imperative `close`. */
  onRequestClose: (close?: () => void) => void;
}

/**
 * The dialog box. reactjs-popup only mounts the render-prop content while the popup
 * is open, so mounting this component tracks the open state precisely for BOTH
 * controlled and trigger modes — letting it own the open-scoped side effects
 * (ref-counted scroll lock + focus trap) via simple mount/unmount.
 */
const ModalContent: FC<ModalContentProps> = ({
  activePlacement,
  framed,
  size,
  className,
  onBack,
  withoutCross,
  crossOutside,
  closeClassName,
  ariaLabel,
  ariaLabelledby,
  children,
  close,
  onRequestClose
}) => {
  const dialogRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    lockBodyScroll();
    return unlockBodyScroll;
  }, []);

  useFocusTrap(dialogRef);

  return (
    <div
      ref={dialogRef}
      role='dialog'
      aria-modal='true'
      aria-label={ariaLabel}
      aria-labelledby={ariaLabelledby}
      tabIndex={-1}
      className={twcls(
        'relative w-full h-full outline-none',
        activePlacement !== 'bottom-sheet' && 'Modal-enter',
        framed &&
          'bg-white overflow-visible flex flex-col p-8 md:p-6 sm:px-4 sm:pt-6 sm:pb-8 rounded-3xl md:rounded-2xl sm:rounded-lg',
        // Legacy default width — only when the caller gave no explicit `size`.
        framed && !size && 'max-w-[480px]',
        activePlacement === 'fullscreen' &&
          'sm:max-h-full sm:overflow-auto sm:rounded-none md:rounded-none fromLg:rounded-none',
        size,
        className
      )}
    >
      {onBack && (
        <button
          type='button'
          aria-label='Назад'
          onClick={onBack}
          className={twcls(ICON_BUTTON_RESET, MODAL_BACK_CLASSES)}
        >
          <Picture icon={arrowLeftLong} height='24' width='24' />
        </button>
      )}
      {!withoutCross && (
        <button
          type='button'
          aria-label='Закрыть'
          onClick={() => {
            onRequestClose(close);
          }}
          className={twcls(
            ICON_BUTTON_RESET,
            MODAL_CROSS_CLASSES,
            crossOutside &&
              'fromLg:-top-8 fromLg:-right-8 md:fromMd:-top-6 md:fromMd:-right-6 sm:top-2 sm:right-2',
            closeClassName
          )}
        >
          <Picture
            pictureClassName={twcls(
              crossOutside && 'fromMd:[&>img]:w-8 fromMd:[&>img]:h-8'
            )}
            icon={crossOutside ? closeOutlineDG : closeOutlineMidGrey}
            height='24'
            width='24'
          />
        </button>
      )}
      {typeof children === 'function'
        ? children(() => {
            onRequestClose(close);
          })
        : children}
    </div>
  );
};

const Modal: FC<ModalProps> = ({
  open,
  children,
  onClose,
  onBack,
  ariaLabel,
  ariaLabelledby,
  withoutCross = false,
  framed = false,
  size,
  className,
  crossOutside,
  closeClassName,
  contentStyle = {},
  overlayStyle = {},
  placementMobile = 'center',
  placementTablet = 'center',
  placementDesktop = 'center',
  popupProps = {}
}) => {
  const { isMobile, isTablet } = useWindowSize();

  const activePlacement = isMobile
    ? placementMobile
    : isTablet
    ? placementTablet
    : placementDesktop;

  const placementStyle = PLACEMENT_CONTENT_STYLE[activePlacement];

  const handleClose = useCallback(
    (close?: () => void) => {
      // reactjs-popup's imperative `close` (closePopup) already invokes our
      // `onClose` prop (via the <Popup onClose>) on the next tick. Calling
      // `onClose` here too would fire the consumer handler twice per close —
      // which silently breaks toggle-style handlers (close then re-open). So
      // when we have the imperative close, defer to it as the single source;
      // otherwise (ESC / outside-click path) call `onClose` directly.
      if (typeof close === 'function') {
        close();
      } else {
        onClose?.();
      }
    },
    [onClose]
  );

  const popupOpenProp =
    typeof open === 'boolean'
      ? { open }
      : popupProps.trigger
      ? {}
      : { open: true };

  return (
    <Popup
      modal
      nested
      lockScroll={false}
      closeOnDocumentClick
      {...popupProps}
      {...popupOpenProp}
      className={activePlacement === 'bottom-sheet' ? SHEET_POPUP_CLASS : ''}
      onClose={() => {
        handleClose();
      }}
      contentStyle={{
        zIndex: 'var(--z-modal-content)',
        ...placementStyle,
        ...contentStyle
      }}
      overlayStyle={{
        zIndex: 'var(--z-modal-overlay)',
        width: '100%',
        height: '100%',
        ...overlayStyle
      }}
    >
      {(close: () => void) => (
        <ModalContent
          activePlacement={activePlacement}
          framed={framed}
          size={size}
          className={className}
          onBack={onBack}
          withoutCross={withoutCross}
          crossOutside={crossOutside}
          closeClassName={closeClassName}
          ariaLabel={ariaLabel}
          ariaLabelledby={ariaLabelledby}
          close={close}
          onRequestClose={handleClose}
        >
          {children}
        </ModalContent>
      )}
    </Popup>
  );
};

export default Modal;
